import { supabase } from '../lib/supabase';

/**
 * ─── CANONICAL ERROR-SURFACING PATTERN ──────────────────────────────────────
 *
 * The rest of this codebase has a recurring bug class: catch blocks that
 * return `{ data: [], error: { message: error?.message } }` — silently
 * stripping `code`, `details`, `hint`, and the stack. Callers can't tell an
 * RLS denial (PGRST301) from a network error from a missing column, so the
 * UI shows an empty state while the operation actually failed.
 *
 * This service is the reference implementation. Mirror this pattern in
 * follow-up PRs that propagate it across the codebase.
 *
 * 1. SERVICES — return the FULL error object, preserving code/message/details/hint.
 *    - Keep the existing `{ data, error }` shape (Supabase-native; matches
 *      what callers already destructure).
 *    - Use the `surfaceError` helper below: it logs structured context and
 *      returns the real error so callers can branch on `error.code`.
 *    - Do NOT wrap the error in `{ message }` — that throws away everything
 *      a caller needs to react meaningfully.
 *
 * 2. PAGE-LAYER CALLERS — branch on `error?.code` where it matters.
 *    - PGRST301 → RLS denial → "You don't have access to this <thing>."
 *    - Other codes → fall back to a friendly intro PLUS `error.message`,
 *      e.g. `Failed to load sites: ${error.message}`.
 *    - Always log the real error to console with `[Component] action failed`
 *      so engineering can diagnose without a repro.
 *
 * 3. UI — never replace `error.message` with a generic string. If you want a
 *    friendly intro, show it AS WELL AS the real message, not INSTEAD OF.
 *
 * The Stripe webhook handler (supabase/functions/stripe-webhook/index.ts) is
 * the existing precedent for this in the codebase: it logs the real error
 * and returns it in the response, never masking with a generic string.
 */

/** Log structured error context and return the original error object unchanged. */
function surfaceError(scope, error) {
  console.error(`[clientService] ${scope} failed:`, {
    code: error?.code,
    message: error?.message,
    details: error?.details,
    hint: error?.hint,
  });
  return error;
}

export const clientService = {
  /**
   * Get all sites explicitly shared with the current client user.
   */
  async getSharedSites() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('client_site_shares')
        ?.select(`
          id,
          shared_at,
          notes,
          can_view_timeline,
          can_view_photos,
          can_view_documents,
          can_view_reports,
          site:sites!client_site_shares_site_id_fkey(
            id, site_name, site_reference, address_line1, city, postcode,
            status, compliance_status, start_date, end_date, worker_count,
            company_id,
            client_can_view_overview,
            client_can_view_notes,
            client_can_view_snags, client_can_view_near_misses,
            client_can_view_daily_progress, client_can_view_rams,
            client_can_view_toolbox_talks, client_can_view_photos,
            client_can_view_documents, client_can_view_drawings,
            client_can_view_reports, client_can_view_activity_timeline,
            client_can_view_site_diary, client_can_view_inductions,
            client_can_view_compliance
          )
        `)
        ?.eq('client_user_id', user?.id)
        ?.order('shared_at', { ascending: false });

      if (error) throw error;
      // Hide archived sites from the client portal — NSherjFh cascade
      // visibility rule. The share row stays in the DB; if the admin
      // unarchives the site, it reappears automatically.
      const visible = (data || [])?.filter(s => s?.site && s?.site?.status !== 'archived');
      return {
        data: visible?.map(s => ({
          ...s?.site,
          shareId: s?.id,
          sharedAt: s?.shared_at,
          shareNotes: s?.notes,
          permissions: {
            canViewTimeline: s?.can_view_timeline ?? true,
            canViewPhotos: s?.can_view_photos ?? true,
            canViewDocuments: s?.can_view_documents ?? true,
            canViewReports: s?.can_view_reports ?? true,
          },
          siteVisibility: {
            canViewOverview: s?.site?.client_can_view_overview ?? true,
            canViewNotes: s?.site?.client_can_view_notes ?? true,
            canViewSnags: s?.site?.client_can_view_snags ?? true,
            canViewNearMisses: s?.site?.client_can_view_near_misses ?? true,
            canViewDailyProgress: s?.site?.client_can_view_daily_progress ?? true,
            canViewRams: s?.site?.client_can_view_rams ?? true,
            canViewToolboxTalks: s?.site?.client_can_view_toolbox_talks ?? true,
            canViewPhotos: s?.site?.client_can_view_photos ?? true,
            canViewDocuments: s?.site?.client_can_view_documents ?? true,
            canViewDrawings: s?.site?.client_can_view_drawings ?? true,
            canViewReports: s?.site?.client_can_view_reports ?? true,
            canViewActivityTimeline: s?.site?.client_can_view_activity_timeline ?? true,
            canViewSiteDiary: s?.site?.client_can_view_site_diary ?? true,
            canViewInductions: s?.site?.client_can_view_inductions ?? true,
            canViewCompliance: s?.site?.client_can_view_compliance ?? true,
          }
        })),
        error: null
      };
    } catch (error) {
      return { data: [], error: surfaceError('getSharedSites', error) };
    }
  },

  /**
   * Fetch fresh visibility settings for a single site.
   * Used to refresh permissions without reloading all sites.
   */
  async getSiteVisibility(siteId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('client_site_shares')
        ?.select(`
          site:sites!client_site_shares_site_id_fkey(
            client_can_view_overview,
            client_can_view_notes,
            client_can_view_snags,
            client_can_view_near_misses,
            client_can_view_daily_progress,
            client_can_view_rams,
            client_can_view_toolbox_talks,
            client_can_view_photos,
            client_can_view_documents,
            client_can_view_drawings,
            client_can_view_reports,
            client_can_view_activity_timeline,
            client_can_view_site_diary,
            client_can_view_inductions,
            client_can_view_compliance
          )
        `)
        ?.eq('client_user_id', user?.id)
        ?.eq('site_id', siteId)
        ?.maybeSingle();

      if (error) throw error;
      if (!data?.site) {
        // Not an exception — the row simply doesn't exist (no share or RLS-filtered).
        // Surface a typed error so callers can distinguish from a fetch failure.
        const noAccess = new Error('No access to this site');
        noAccess.code = 'NO_SHARE';
        return { data: null, error: noAccess };
      }

      const s = data?.site;
      return {
        data: {
          canViewOverview: s?.client_can_view_overview ?? true,
          canViewNotes: s?.client_can_view_notes ?? true,
          canViewSnags: s?.client_can_view_snags ?? true,
          canViewNearMisses: s?.client_can_view_near_misses ?? true,
          canViewDailyProgress: s?.client_can_view_daily_progress ?? true,
          canViewRams: s?.client_can_view_rams ?? true,
          canViewToolboxTalks: s?.client_can_view_toolbox_talks ?? true,
          canViewPhotos: s?.client_can_view_photos ?? true,
          canViewDocuments: s?.client_can_view_documents ?? true,
          canViewDrawings: s?.client_can_view_drawings ?? true,
          canViewReports: s?.client_can_view_reports ?? true,
          canViewActivityTimeline: s?.client_can_view_activity_timeline ?? true,
          canViewSiteDiary: s?.client_can_view_site_diary ?? true,
          canViewInductions: s?.client_can_view_inductions ?? true,
          canViewCompliance: s?.client_can_view_compliance ?? true,
        },
        error: null
      };
    } catch (error) {
      return { data: null, error: surfaceError('getSiteVisibility', error) };
    }
  },

  /**
   * Verify the current client has access to a specific site.
   * Returns { hasAccess, permissions, error }. `error` is null on a clean
   * "no, they don't have access" answer, and populated only when the check
   * itself failed (so callers can distinguish "denied" from "couldn't ask").
   */
  async hasAccessToSite(siteId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return { hasAccess: false, permissions: null, error: null };

      const { data, error } = await supabase
        ?.from('client_site_shares')
        ?.select('id, can_view_timeline, can_view_photos, can_view_documents, can_view_reports')
        ?.eq('client_user_id', user?.id)
        ?.eq('site_id', siteId)
        ?.maybeSingle();

      if (error) throw error;
      return {
        hasAccess: !!data,
        permissions: data ? {
          canViewTimeline: data?.can_view_timeline ?? true,
          canViewPhotos: data?.can_view_photos ?? true,
          canViewDocuments: data?.can_view_documents ?? true,
          canViewReports: data?.can_view_reports ?? true,
        } : null,
        error: null,
      };
    } catch (error) {
      return { hasAccess: false, permissions: null, error: surfaceError('hasAccessToSite', error) };
    }
  },

  /**
   * Get activity timeline for a shared site.
   */
  async getSiteActivity(siteId, filters = {}) {
    try {
      let query = supabase
        ?.from('activities')
        ?.select(`
          *,
          user:user_profiles!activities_user_id_fkey(id, full_name, role),
          site:sites!activities_site_id_fkey(id, site_name)
        `)
        ?.eq('site_id', siteId)
        ?.order('created_at', { ascending: false });

      if (filters?.activityType && filters?.activityType !== 'all') {
        if (Array.isArray(filters?.activityType)) {
          query = query?.in('activity_type', filters?.activityType);
        } else {
          query = query?.eq('activity_type', filters?.activityType);
        }
      }

      if (filters?.dateFrom) {
        query = query?.gte('created_at', new Date(filters?.dateFrom)?.toISOString());
      }

      if (filters?.dateTo) {
        const endDate = new Date(filters?.dateTo);
        endDate?.setHours(23, 59, 59, 999);
        query = query?.lte('created_at', endDate?.toISOString());
      }

      query = query?.limit(filters?.limit || 100);

      const { data, error } = await query;
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: surfaceError('getSiteActivity', error) };
    }
  },

  /**
   * Get task photos for a shared site.
   */
  async getSitePhotos(siteId, filters = {}) {
    try {
      let query = supabase
        ?.from('task_photos')
        ?.select(`
          *,
          task:daily_tasks!task_photos_task_id_fkey(id, title, category),
          uploader:user_profiles!task_photos_uploaded_by_fkey(id, full_name)
        `)
        ?.eq('site_id', siteId)
        ?.eq('client_visible', true)
        ?.order('created_at', { ascending: false });

      if (filters?.dateFrom) {
        query = query?.gte('created_at', new Date(filters?.dateFrom)?.toISOString());
      }

      if (filters?.dateTo) {
        const endDate = new Date(filters?.dateTo);
        endDate?.setHours(23, 59, 59, 999);
        query = query?.lte('created_at', endDate?.toISOString());
      }

      query = query?.limit(filters?.limit || 200);

      const { data, error } = await query;
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: surfaceError('getSitePhotos', error) };
    }
  },

  /**
   * Get a signed URL for a task photo.
   */
  async getPhotoUrl(filePath) {
    try {
      const { data, error } = await supabase?.storage
        ?.from('task-photos')
        ?.createSignedUrl(filePath, 3600);
      if (error) throw error;
      return { url: data?.signedUrl, error: null };
    } catch (error) {
      return { url: null, error: surfaceError('getPhotoUrl', error) };
    }
  },

  /**
   * Get documents for a shared site.
   */
  async getSiteDocuments(siteId, filters = {}) {
    try {
      let query = supabase
        ?.from('documents')
        ?.select('*')
        ?.eq('site_id', siteId)
        // NSherjFh PR 2: hide soft-deleted docs from the client portal.
        ?.is('deleted_at', null)
        ?.order('created_at', { ascending: false });

      if (filters?.category && filters?.category !== 'all') {
        query = query?.eq('category', filters?.category);
      }

      if (filters?.status && filters?.status !== 'all') {
        query = query?.eq('status', filters?.status);
      }

      const { data, error } = await query;
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: surfaceError('getSiteDocuments', error) };
    }
  },

  /**
   * Get a signed URL for a document.
   */
  async getDocumentUrl(filePath) {
    try {
      const { data, error } = await supabase?.storage
        ?.from('construction-documents')
        ?.createSignedUrl(filePath, 3600);
      if (error) throw error;
      return { url: data?.signedUrl, error: null };
    } catch (error) {
      return { url: null, error: surfaceError('getDocumentUrl', error) };
    }
  },

  /**
   * Get compliance data for a shared site (audits + snags summary).
   * Runs four reads in parallel; surfaces the first non-null sub-error so
   * partial-failure cases don't get masked as "no data".
   */
  async getSiteCompliance(siteId) {
    try {
      const [auditsRes, snagsRes, incidentsRes, docsRes] = await Promise.all([
        supabase
          ?.from('site_audits')
          ?.select('id, audit_name, audit_date, auditor_name, status, overall_result, notes, created_at')
          ?.eq('site_id', siteId)
          // NSherjFh PR 7: client portal compliance widget hides soft-deleted audits.
          ?.is('deleted_at', null)
          ?.order('audit_date', { ascending: false }),
        supabase
          ?.from('snags')
          ?.select('id, title, status, priority, created_at, resolved_at')
          ?.eq('site_id', siteId)
          // NSherjFh PR 4: client portal compliance widget hides soft-deleted snags.
          ?.is('deleted_at', null)
          ?.order('created_at', { ascending: false }),
        supabase
          ?.from('incidents')
          ?.select('id, incident_type, incident_date, severity_level, description, created_at')
          ?.eq('site_id', siteId)
          // NSherjFh PR 5: client portal compliance widget hides soft-deleted incidents.
          ?.is('deleted_at', null)
          ?.order('incident_date', { ascending: false }),
        supabase
          ?.from('documents')
          ?.select('id, title, category, status, expiry_date')
          ?.eq('site_id', siteId)
          // NSherjFh PR 2: soft-deleted docs don't count toward compliance.
          ?.is('deleted_at', null)
          ?.order('expiry_date', { ascending: true })
      ]);

      const subError = auditsRes?.error || snagsRes?.error || incidentsRes?.error || docsRes?.error;
      if (subError) {
        return { data: null, error: surfaceError('getSiteCompliance', subError) };
      }

      return {
        data: {
          audits: auditsRes?.data || [],
          snags: snagsRes?.data || [],
          incidents: incidentsRes?.data || [],
          documents: docsRes?.data || []
        },
        error: null,
      };
    } catch (error) {
      return { data: null, error: surfaceError('getSiteCompliance', error) };
    }
  },

  /**
   * Admin: Share a site with a client user (with permission toggles).
   */
  async shareSiteWithClient({ siteId, clientUserId, companyId, notes = '', permissions = {} }) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('client_site_shares')
        ?.insert({
          site_id: siteId,
          client_user_id: clientUserId,
          company_id: companyId,
          shared_by: user?.id,
          notes,
          can_view_timeline: permissions?.canViewTimeline ?? true,
          can_view_photos: permissions?.canViewPhotos ?? true,
          can_view_documents: permissions?.canViewDocuments ?? true,
          can_view_reports: permissions?.canViewReports ?? true,
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: surfaceError('shareSiteWithClient', error) };
    }
  },

  /**
   * Admin: Update permission toggles for an existing share.
   */
  async updateSharePermissions(shareId, permissions) {
    try {
      const { data, error } = await supabase
        ?.from('client_site_shares')
        ?.update({
          can_view_timeline: permissions?.canViewTimeline ?? true,
          can_view_photos: permissions?.canViewPhotos ?? true,
          can_view_documents: permissions?.canViewDocuments ?? true,
          can_view_reports: permissions?.canViewReports ?? true,
        })
        ?.eq('id', shareId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: surfaceError('updateSharePermissions', error) };
    }
  },

  /**
   * Admin: Remove a site share from a client.
   */
  async removeSiteShare(shareId) {
    try {
      const { error } = await supabase
        ?.from('client_site_shares')
        ?.delete()
        ?.eq('id', shareId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: surfaceError('removeSiteShare', error) };
    }
  },

  /**
   * Admin: Get all client shares for a site (with permission columns).
   */
  async getSharesForSite(siteId) {
    try {
      const { data, error } = await supabase
        ?.from('client_site_shares')
        ?.select(`
          id, shared_at, notes,
          can_view_timeline, can_view_photos, can_view_documents, can_view_reports,
          client:user_profiles!client_site_shares_client_user_id_fkey(id, full_name, email)
        `)
        ?.eq('site_id', siteId)
        ?.order('shared_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: surfaceError('getSharesForSite', error) };
    }
  },

  /**
   * Admin: Get all client users in the organisation (role = 'client').
   */
  async getClientUsers(companyId) {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name, email')
        ?.eq('company_id', companyId)
        ?.eq('role', 'client')
        ?.order('full_name', { ascending: true });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: surfaceError('getClientUsers', error) };
    }
  }
};
